import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { API_URLS } from '../../Services/servicioURL';

interface Producto {
    cve: string;
    descripcion: string;
    modelo?: string;
}

interface Sucursal {
    id: number;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

interface Traspaso {
    id: number;
    cve_producto: string;
    descripcion: string;
    id_sucursal_destino: number;
    cve_departamento_destino: string;
    fecha: string;
    nombre_usuario: string;
    observaciones?: string;
    sucursalNombre?: string;
    departamentoNombre?: string;
}

const Traspasos: React.FC = () => {
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [loading, setLoading] = useState(true);
    
    // Estados del formulario
    const [formData, setFormData] = useState({
        id: 1,
        cveProducto: '',
        descripcionProducto: '',
        selectedSucursal: null as number | null,
        selectedDepartamento: null as string | null,
        fecha: new Date().toISOString().split('T')[0],
        nombreUsuario: '',
        observaciones: ''
    });
    
    // Estados de catálogos
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [productosCatalogo, setProductosCatalogo] = useState<Producto[]>([]);
    
    // Estados de modales y búsquedas
    const [mostrarBuscadorProducto, setMostrarBuscadorProducto] = useState(false);
    const [mostrarTablaConsulta, setMostrarTablaConsulta] = useState(false);
    const [searchTermProducto, setSearchTermProducto] = useState('');
    const [searchTermTraspaso, setSearchTermTraspaso] = useState('');
    const [traspasos, setTraspasos] = useState<Traspaso[]>([]);
    const [editingTraspasoId, setEditingTraspasoId] = useState<number | null>(null);
    
    const toast = useRef<Toast>(null);

    // Filtros optimizados con useMemo
    const filteredProductos = useMemo(() => {
        if (!searchTermProducto) return productosCatalogo;
        const searchLower = searchTermProducto.toLowerCase();
        return productosCatalogo.filter(p =>
            p.cve.toLowerCase().includes(searchLower) ||
            p.descripcion.toLowerCase().includes(searchLower)
        );
    }, [productosCatalogo, searchTermProducto]);

    const filteredTraspasos = useMemo(() => {
        if (!searchTermTraspaso) return traspasos;
        const searchLower = searchTermTraspaso.toLowerCase();
        return traspasos.filter(t =>
            t.cve_producto.toLowerCase().includes(searchLower) ||
            t.descripcion.toLowerCase().includes(searchLower) ||
            t.nombre_usuario.toLowerCase().includes(searchLower)
        );
    }, [traspasos, searchTermTraspaso]);

    // Función para mostrar mensajes
    const showMessage = (severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    // Cargar datos iniciales
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [prods, sucs, deps] = await Promise.all([
                    fetch(API_URLS.productos.obtener).then(r => r.json()),
                    fetch(API_URLS.sucursales.obtener).then(r => r.json()),
                    fetch(API_URLS.departamentos.obtener).then(r => r.json())
                ]);

                setProductosCatalogo(prods);
                setSucursales(sucs);
                setDepartamentos(deps);
            } catch (error) {
                console.error('Error al cargar datos:', error);
                showMessage('error', 'Error', 'No se pudieron cargar los datos');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Cargar último ID para nuevos registros
    useEffect(() => {
        const cargarIdAutomatico = async () => {
            try {
                const response = await fetch(API_URLS.traspasos.obtener);
                const data = await response.json();
                const ids = data.map((t: any) => parseInt(t.id)).filter(Number);
                const ultimoId = ids.length ? Math.max(...ids) : 0;
                setFormData(prev => ({ ...prev, id: ultimoId + 1 }));
            } catch {
                setFormData(prev => ({ ...prev, id: 1 }));
            }
        };
        if (mode === 'new') cargarIdAutomatico();
    }, [mode]);

    // Cargar usuario del localStorage
    useEffect(() => {
        const nombreUsuarioStorage = localStorage.getItem('nombreUsuario');
        if (nombreUsuarioStorage && mode === 'new') {
            setFormData(prev => ({ ...prev, nombreUsuario: nombreUsuarioStorage }));
        }
    }, [mode]);

    // Función para limpiar formulario
    const limpiarFormulario = () => {
        setFormData({
            id: 1,
            cveProducto: '',
            descripcionProducto: '',
            selectedSucursal: null,
            selectedDepartamento: null,
            fecha: new Date().toISOString().split('T')[0],
            nombreUsuario: '',
            observaciones: ''
        });
        setEditingTraspasoId(null);
        setMode('view');
    };

    // Handlers principales
    const handleNuevo = () => {
        limpiarFormulario();
        setMode('new');
        const nombreUsuarioStorage = localStorage.getItem('nombreUsuario');
        if (nombreUsuarioStorage) {
            setFormData(prev => ({ ...prev, nombreUsuario: nombreUsuarioStorage }));
        }
    };

    const handleEditar = () => {
        if (!editingTraspasoId) {
            showMessage('warn', 'Advertencia', 'Seleccione un traspaso para editar');
            return;
        }
        setMode('edit');
    };

    const handleEliminar = async () => {
        if (!editingTraspasoId) {
            showMessage('warn', 'Advertencia', 'Seleccione un traspaso para eliminar');
            return;
        }

        const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar este traspaso?\n\nEsta acción no se puede deshacer.');
        if (!confirmacion) return;

        try {
            const response = await fetch(API_URLS.traspasos.eliminar(editingTraspasoId.toString()), {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error('Error al eliminar');
            
            showMessage('success', '¡Traspaso eliminado!', 'El traspaso se eliminó correctamente');
            limpiarFormulario();
        } catch (error) {
            console.error('Error:', error);
            showMessage('error', 'Error', 'No se pudo eliminar el traspaso');
        }
    };

    const handleGuardar = async () => {
        const { cveProducto, selectedSucursal, selectedDepartamento, fecha, nombreUsuario, observaciones } = formData;
        
        if (!cveProducto || !selectedSucursal || !selectedDepartamento || !fecha || !nombreUsuario) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos marcados con * son obligatorios');
            return;
        }

        const payload = {
            ...(mode !== 'new' && editingTraspasoId ? { id: editingTraspasoId } : {}),
            cve_producto: cveProducto,
            id_sucursal_destino: selectedSucursal,
            cve_departamento_destino: selectedDepartamento,
            fecha,
            nombre_usuario: nombreUsuario,
            observaciones: observaciones || ''
        };

        try {
            const url = mode === 'new' 
                ? API_URLS.traspasos.registrar 
                : API_URLS.traspasos.actualizar(editingTraspasoId!.toString());
            
            const response = await fetch(url, {
                method: mode === 'new' ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`Error al ${mode === 'new' ? 'registrar' : 'actualizar'}`);
            
            showMessage('success', '¡Éxito!', `Traspaso ${mode === 'new' ? 'registrado' : 'actualizado'} exitosamente`);
            limpiarFormulario();
        } catch (error) {
            console.error('Error:', error);
            showMessage('error', 'Error', `No se pudo ${mode === 'new' ? 'registrar' : 'actualizar'} el traspaso`);
        }
    };

    // Búsqueda de traspasos
    const handleBuscarTraspasos = async () => {
        try {
            const response = await fetch(API_URLS.traspasos.obtener);
            const data: Traspaso[] = await response.json();

            const enrichedTraspasos = data.map(traspaso => {
                const producto = productosCatalogo.find(p => p.cve === traspaso.cve_producto);
                const sucursal = sucursales.find(s => s.id === traspaso.id_sucursal_destino);
                const departamento = departamentos.find(d => d.cve === traspaso.cve_departamento_destino);
                return {
                    ...traspaso,
                    descripcion: producto?.descripcion || 'Descripción no disponible',
                    sucursalNombre: sucursal?.nombre || 'Sucursal no disponible',
                    departamentoNombre: departamento?.nombre || 'Departamento no disponible'
                };
            });

            setTraspasos(enrichedTraspasos);
            setMostrarTablaConsulta(true);
        } catch (error) {
            console.error('Error:', error);
            showMessage('error', 'Error', 'No se pudieron cargar los traspasos');
        }
    };

    const handleSeleccionarTraspaso = (traspaso: Traspaso) => {
        setFormData({
            id: traspaso.id,
            cveProducto: traspaso.cve_producto,
            descripcionProducto: traspaso.descripcion,
            selectedSucursal: traspaso.id_sucursal_destino,
            selectedDepartamento: traspaso.cve_departamento_destino,
            fecha: traspaso.fecha,
            nombreUsuario: traspaso.nombre_usuario,
            observaciones: traspaso.observaciones || ''
        });
        setEditingTraspasoId(traspaso.id);
        setMostrarTablaConsulta(false);
        setMode('view');
    };

    // Selección de productos
    const seleccionarProducto = (producto: Producto) => {
        setFormData(prev => ({
            ...prev,
            cveProducto: producto.cve,
            descripcionProducto: producto.descripcion
        }));
        setMostrarBuscadorProducto(false);
        showMessage('info', 'Producto seleccionado', `Se ha seleccionado: ${producto.descripcion}`);
    };

    const abrirBuscadorProductos = () => {
        if (mode === 'view') {
            showMessage('warn', 'Advertencia', 'No se puede buscar productos en modo consulta');
            return;
        }
        setMostrarBuscadorProducto(true);
    };

    // Función para obtener información del modo actual
    const getModeIndicator = () => {
        switch (mode) {
            case 'new': return { text: 'NUEVO TRASPASO', color: '#10b981', icon: 'pi-plus' };
            case 'edit': return { text: 'EDITANDO TRASPASO', color: '#f59e0b', icon: 'pi-pencil' };
            default: return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    if (loading) {
        return (
            <>
                <Navbar />
                <Sidebar />
                <Toast ref={toast} />
                <div className="content-wrapper" style={{
                    backgroundImage: 'url(/dist/img/FondoLogin.png)',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed',
                    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{ textAlign: 'center', color: '#1f2937' }}>
                        <i className="fas fa-spinner fa-spin fa-3x mb-3"></i>
                        <h4>Cargando datos...</h4>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            {/* Modal - Buscador de Productos */}
            {mostrarBuscadorProducto && (
                <div className="overlay" onClick={() => setMostrarBuscadorProducto(false)} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '700px', width: '90%', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                📦 Catálogo de Productos
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => setMostrarBuscadorProducto(false)}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <InputText
                                style={{ width: '100%', height: '40px', padding: '0 12px', border: '2px solid #e5e7eb', borderRadius: '8px' }}
                                placeholder="Buscar por clave o descripción..."
                                value={searchTermProducto}
                                onChange={(e) => setSearchTermProducto(e.target.value)}
                            />
                        </div>

                        <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10 }}>
                                    <tr>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>🔑 Clave</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>📝 Descripción</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>🏷️ Modelo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProductos.map((producto, index) => (
                                        <tr 
                                            key={index} 
                                            onClick={() => seleccionarProducto(producto)}
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                        >
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.cve}</td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.descripcion}</td>
                                            <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.modelo || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal - Consulta de Traspasos */}
            {mostrarTablaConsulta && (
                <div className="overlay" onClick={() => setMostrarTablaConsulta(false)} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '1000px', width: '95%', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                🔄 Consultar Traspasos
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => setMostrarTablaConsulta(false)}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <InputText
                                style={{ width: '100%', height: '40px', padding: '0 12px', border: '2px solid #e5e7eb', borderRadius: '8px' }}
                                placeholder="Buscar por producto, descripción o usuario..."
                                value={searchTermTraspaso}
                                onChange={(e) => setSearchTermTraspaso(e.target.value)}
                            />
                        </div>

                        <div style={{ flex: 1, overflow: 'auto' }}>
                            <DataTable 
                                value={filteredTraspasos} 
                                responsiveLayout="scroll" 
                                stripedRows 
                                size="small" 
                                emptyMessage="📭 No hay traspasos registrados"
                                onRowClick={(e) => handleSeleccionarTraspaso(e.data as Traspaso)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Column field="id" header="🆔 ID" />
                                <Column field="cve_producto" header="📦 Producto" />
                                <Column field="descripcion" header="📝 Descripción" />
                                <Column field="sucursalNombre" header="🏢 Sucursal" />
                                <Column field="departamentoNombre" header="🏛️ Departamento" />
                                <Column field="nombre_usuario" header="👤 Usuario" />
                                <Column field="fecha" header="📅 Fecha" />
                            </DataTable>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenido principal */}
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover', backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed',
                minHeight: '100vh', position: 'relative', padding: '20px'
            }}>
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px', padding: '24px', maxWidth: '1000px',
                    margin: '0 auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Header con indicador de modo */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                                🔄 Gestión de Traspasos
                            </h2>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '8px 16px', backgroundColor: modeInfo.color,
                                color: 'white', borderRadius: '20px',
                                fontSize: '0.85rem', fontWeight: '600'
                            }}>
                                <i className={`pi ${modeInfo.icon}`}></i>
                                {modeInfo.text}
                            </div>
                        </div>
                        <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                    </div>

                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <form autoComplete="off">
                            {/* Primera fila: ID, Fecha, Usuario */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr', gap: '20px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🆔 ID
                                    </label>
                                    <InputText
                                        style={{ width: '100%', height: '40px', padding: '0 12px', border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}
                                        value={formData.id.toString()}
                                        disabled
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        📅 Fecha <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        type="date"
                                        style={{
                                            width: '100%', height: '40px', padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={formData.fecha}
                                        onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                                        disabled={mode === 'view'}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        👤 Usuario <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%', height: '40px', padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={formData.nombreUsuario}
                                        onChange={(e) => setFormData(prev => ({ ...prev, nombreUsuario: e.target.value }))}
                                        disabled={mode === 'view'}
                                        placeholder="Nombre de usuario"
                                    />
                                </div>
                            </div>

                            {/* Segunda fila: Producto */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '20px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        📦 Producto <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <InputText
                                            style={{ flex: 1, height: '40px', padding: '0 12px', border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}
                                            value={formData.cveProducto}
                                            disabled
                                            placeholder="Clave del producto"
                                        />
                                        <Button
                                            icon="pi pi-search"
                                            className="p-button-info"
                                            onClick={abrirBuscadorProductos}
                                            disabled={mode === 'view'}
                                            style={{ height: '40px', minWidth: '40px' }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        📝 Descripción
                                    </label>
                                    <InputText
                                        style={{ width: '100%', height: '40px', padding: '0 12px', border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}
                                        value={formData.descripcionProducto}
                                        disabled
                                        placeholder="Descripción del producto"
                                    />
                                </div>
                            </div>

                            {/* Tercera fila: Destino */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🏢 Sucursal Destino <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={formData.selectedSucursal}
                                        options={sucursales}
                                        optionLabel="nombre"
                                        optionValue="id"
                                        placeholder="Selecciona una sucursal"
                                        onChange={(e) => setFormData(prev => ({ ...prev, selectedSucursal: e.value }))}
                                        disabled={mode === 'view'}
                                        style={{
                                            width: '100%', height: '40px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🏛️ Departamento Destino <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={formData.selectedDepartamento}
                                        options={departamentos}
                                        optionLabel="nombre"
                                        optionValue="cve"
                                        placeholder="Selecciona un departamento"
                                        onChange={(e) => setFormData(prev => ({ ...prev, selectedDepartamento: e.value }))}
                                        disabled={mode === 'view'}
                                        style={{
                                            width: '100%', height: '40px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Cuarta fila: Observaciones */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    📋 Observaciones
                                </label>
                                <InputTextarea
                                    value={formData.observaciones}
                                    onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                                    rows={3}
                                    disabled={mode === 'view'}
                                    placeholder="Observaciones adicionales..."
                                    style={{
                                        width: '100%', padding: '12px',
                                        border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px',
                                        backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </form>
                    </div>

                    {/* Botones debajo de los inputs */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button icon="pi pi-search" label="Consultar" className="p-button-info p-button-raised" onClick={handleBuscarTraspasos} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                                <Button icon="pi pi-plus" label="Nuevo" className="p-button-success p-button-raised" onClick={handleNuevo} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                                <Button icon="pi pi-pencil" label="Editar" className="p-button-warning p-button-raised" onClick={handleEditar} disabled={!editingTraspasoId} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !editingTraspasoId ? 0.5 : 1 }} />
                                <Button icon="pi pi-trash" label="Eliminar" className="p-button-danger p-button-raised" onClick={handleEliminar} disabled={!editingTraspasoId} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !editingTraspasoId ? 0.5 : 1 }} />
                            </>
                        ) : (
                            <>
                                <Button icon="pi pi-check" label="Guardar" className="p-button-success p-button-raised" onClick={handleGuardar} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                                <Button icon="pi pi-times" label="Cancelar" className="p-button-secondary p-button-raised" onClick={limpiarFormulario} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Traspasos;